import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import homeServices from '../services/homeServices';
import { ApiState } from '../constants/enums';
import {
    ITaxNews,
    ITaxNewsResponse,
    ITaxSchedulesResponse,
    ITaxSchedules,
    IDeleteScheduleRequest,
    IDeleteTaxSchedulesResponse,
    ITaxNewsCountriesResponse,
    ITaxNewsParams,
    IUploadedDTSchedulesResponse,
    IUploadedDTSchedules,
} from '../types/home.type';
import _ from 'lodash';

export interface HomeState {
    apiState: {
        getTaxNews: ApiState;
        getTaxNewsCountries: ApiState;
        getTaxSchedules: ApiState;
        getUploadedDTSchedules: ApiState;
        deleteTaxSchedule: ApiState;
    };
    taxNewsCountries: {
        data: any[];
        totalElements: number;
        pageNumber: number;
        pageSize: number;
    };
    taxNews: ITaxNews[];
    taxSchedules: ITaxSchedules;
    uploadedDTSchedules: IUploadedDTSchedules;
    isLoggedOut: boolean;
    isScheduleView: boolean;
    selectedCountry: string;    
}

const initialState: HomeState = {
    apiState: {
        getTaxNews: ApiState.NOT_STARTED,
        getTaxNewsCountries: ApiState.NOT_STARTED,
        getTaxSchedules: ApiState.NOT_STARTED,
        getUploadedDTSchedules: ApiState.NOT_STARTED,
        deleteTaxSchedule: ApiState.NOT_STARTED,
    },
    taxNewsCountries: {
        data: [],
        totalElements: 0,
        pageNumber: 0,
        pageSize: 10,
    },
    taxNews: [],
    taxSchedules: {
        data: [],
        totalElements: 0,
        pageNumber: 0,
        pageSize: 10,
    },
    uploadedDTSchedules:{
        data: [],
        totalElements: 0,
        pageNumber: 0,
        pageSize: 10,
    },
    isLoggedOut: false,
    isScheduleView: true,
    selectedCountry: '',
};

export const getTaxNews = createAsyncThunk<ITaxNewsResponse, { 
    inputParameters: ITaxNewsParams;
 }
 >(
    'home-page/get-tax-news', async (inputParameters) => {
    const response = await homeServices.getTaxNews(inputParameters);
    return response;
});

export const deleteTaxSchedule = createAsyncThunk<IDeleteTaxSchedulesResponse, IDeleteScheduleRequest>(
    'home-page/delete-schedule',
    async ({ scheduleId, isDeleted }) => {
        const response = await homeServices.deleteTaxSchedule({ scheduleId, isDeleted });
        return response;
    },
);

export const getTaxSchedules = createAsyncThunk<
    ITaxSchedulesResponse,
    {
        isUserGroup: boolean;
        pageSize?: string;
        pageNumber?: string;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('home-page/get-tax-schedules', async ({ isUserGroup, pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await homeServices.getTaxSchedules({
        isUserGroup,
        pageSize,
        pageNumber,
        fieldName,
        sortOrder,
        searchString,
    });
    const respData = {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize ? +pageSize : 10,
        },
    };
    return respData;
});


export const getUploadedDTSchedules = createAsyncThunk<
IUploadedDTSchedulesResponse,
{
        isUserGroup: boolean;
        pageSize?: number;
        pageNumber?: number;
        fieldName?: string;
        sortOrder?: string;
        searchString?: string;
    }
>('home-page/get-Uploaded-DTSchedules', async ({ isUserGroup, pageSize, pageNumber, fieldName, sortOrder, searchString }) => {
    const response = await homeServices.getUploadedDTSchedules({
        isUserGroup,
        pageSize,
        pageNumber,
        fieldName,
        sortOrder,
        searchString,
    });
    const respData = {
        ...response,
        data: {
            ...response.data,
            pageSize: pageSize ? +pageSize : 10,
        },
    };
    return respData;
});

export const getTaxNewsCountries = createAsyncThunk<
    ITaxNewsCountriesResponse,
    {
        countryCodes: string[];
        pagination: {
            pageSize?: string;
            pageNumber?: string;
            fieldName?: string;
            sortOrder?: string;
            searchString?: string;
        };
    }
>('home-page/getTaxNewsCountries', async ({ countryCodes, pagination }) => {
    const { pageSize, pageNumber, fieldName, sortOrder, searchString } = pagination;
    const response = await homeServices.getTaxNewsCountries({
        countryCodes,
        pageSize,
        pageNumber,
        fieldName,
        sortOrder,
        searchString,
    });
    const respData = {
        ...response,
        data: {
            ...response.taxNewsCountByCountry,
            pageSize: pageSize ? +pageSize : 10,
        },
    };
    return respData;
});

const homeSlice = createSlice({
    name: 'home',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getTaxNews.pending, (state) => {
                state.apiState.getTaxNews = ApiState.PENDING;
            })
            .addCase(getTaxNews.fulfilled, (state, action: PayloadAction<ITaxNewsResponse>) => {
                state.taxNews = action.payload.data?.taxNews || [];
                state.apiState.getTaxNews = ApiState.FULFILLED;
            })
            .addCase(getTaxNews.rejected, (state) => {
                state.apiState.getTaxNews = ApiState.REJECTED;
            })
            .addCase(deleteTaxSchedule.pending, (state) => {
                state.apiState.deleteTaxSchedule = ApiState.PENDING;
            })
            .addCase(deleteTaxSchedule.fulfilled, (state) => {
                state.apiState.deleteTaxSchedule = ApiState.FULFILLED;
            })
            .addCase(deleteTaxSchedule.rejected, (state) => {
                state.apiState.deleteTaxSchedule = ApiState.REJECTED;
            })
            .addCase(getTaxSchedules.pending, (state) => {
                state.apiState.getTaxSchedules = ApiState.PENDING;
            })
            .addCase(getTaxSchedules.fulfilled, (state, action: PayloadAction<ITaxSchedulesResponse>) => {
                state.taxSchedules.data = action.payload.data?.taxSchedules || [];
                state.taxSchedules.totalElements = action.payload.data?.totalElements || 0;
                state.taxSchedules.pageNumber = action.payload.data?.pageNumber || 0;
                state.taxSchedules.pageSize = action.payload.data?.pageSize || 0;
                state.apiState.getTaxSchedules = ApiState.FULFILLED;
            })
            .addCase(getTaxSchedules.rejected, (state) => {
                state.apiState.getTaxSchedules = ApiState.REJECTED;
            })
            .addCase(getUploadedDTSchedules.pending, (state) => {
                state.apiState.getUploadedDTSchedules = ApiState.PENDING;
            })
            .addCase(getUploadedDTSchedules.fulfilled, (state, action: PayloadAction<IUploadedDTSchedulesResponse>) => {
                state.uploadedDTSchedules.data = action.payload.data?.dtScheduleRecords || [];
                state.uploadedDTSchedules.totalElements = action.payload.data?.totalElements || 0;
                state.uploadedDTSchedules.pageNumber = action.payload.data?.pageNumber || 0;
                state.uploadedDTSchedules.pageSize = action.payload.data?.pageSize || 0;
                state.apiState.getUploadedDTSchedules = ApiState.FULFILLED;
            })
            .addCase(getUploadedDTSchedules.rejected, (state) => {
                state.apiState.getUploadedDTSchedules = ApiState.REJECTED;
            })
            .addCase(getTaxNewsCountries.pending, (state) => {
                state.apiState.getTaxNewsCountries = ApiState.PENDING;
            })
            .addCase(getTaxNewsCountries.fulfilled, (state, action: PayloadAction<ITaxNewsCountriesResponse>) => {
                state.taxNewsCountries.data = action.payload?.taxNewsCountByCountry || [];
                state.taxNewsCountries.totalElements = action.payload?.totalElements || 0;
                state.taxNewsCountries.pageNumber = action.payload?.pageNumber || 0;
                state.taxNewsCountries.pageSize = action.payload?.pageSize || 0;
                state.apiState.getTaxNewsCountries = ApiState.FULFILLED;
            })
            .addCase(getTaxNewsCountries.rejected, (state) => {
                state.apiState.getTaxNewsCountries = ApiState.REJECTED;
            });
    },
    reducers: {
        setLoggedOut(state, action: PayloadAction<boolean>) {
            state.isLoggedOut = action.payload;
        },
        removeTaxSchedule(state, action: PayloadAction<number>) {
            const copyTaxSchedules = _.cloneDeep(state.taxSchedules);
            copyTaxSchedules.data.splice(action.payload, 1);
            state.taxSchedules = copyTaxSchedules;
        },
        setIsSceduleView(state, action: PayloadAction<boolean>){
            state.isScheduleView = action.payload;
        },
        setSelectedCountry(state, action: PayloadAction<string>){
            state.selectedCountry = action.payload;
        },
    },
});

export const { setLoggedOut, removeTaxSchedule, setIsSceduleView, setSelectedCountry} = homeSlice.actions;

export default homeSlice.reducer;
